<template>
  <div
    class="custom-button"
    :class="{ 'mr-10px': isMargin }"
    :style="{ width }"
  >
    <n-button
      v-bind="filteredAttrs"
      :type="type"
      :ghost="ghost"
      :quaternary="quaternary"
      :text="text"
      :size="size"
      :loading="loading"
      :bordered="bordered"
      :round="round"
      :block="block"
      :color="color"
      :disabled="disabled"
      :style="{ width: '100%' }"
      :class="`${type}-btn`"
      @click="handleClick"
    >
      <template v-if="btnTitle">{{ btnTitle }}</template>
      <slot></slot>
    </n-button>
  </div>
</template>

<script setup lang="ts">
import { omit } from 'lodash-es'
import { ref, useAttrs } from 'vue'
defineOptions({
  inheritAttrs: false
})
interface Props {
  block?: boolean
  width?: string | number
  bordered?: boolean
  circle?: boolean
  color?: string
  disabled?: boolean
  ghost?: boolean
  round?: boolean
  size?: 'tiny' | 'small' | 'medium' | 'large'
  tertiary?: boolean // 是否三级按钮
  quaternary?: boolean // 四级按钮
  text?: boolean // 是否文本
  textColor?: string
  type?:
    | 'default'
    | 'tertiary'
    | 'primary'
    | 'success'
    | 'info'
    | 'warning'
    | 'error'
    | 'custom-primary'
    | 'custom-primary-default'
    | 'custom-primary-mini'
    | 'custom-warning'
    | 'custom-info'
  isMargin?: boolean
  btnTitle?: string
}
const props = withDefaults(defineProps<Props>(), {
  bordered: true,
  circle: false,
  size: 'small',
  type: 'primary',
  width: '100%',
  isMargin: true,
  round: false,
  block: false
})
const loading = ref(false)
const attrs = useAttrs()
const handleClick = async (e: Event) => {
  loading.value = true
  const clickHandler = attrs.onClick as Function | undefined
  console.log(e, clickHandler)
  if (!clickHandler) return

  try {
    const result = clickHandler(e)

    if (result instanceof Promise) {
      await result
    }
  } catch (error) {
    console.error('Button click error:', error)
    throw error // 重新抛出错误让父组件处理
  } finally {
    loading.value = false
  }
}
const filteredAttrs = omit(attrs, ['onClick', 'onclick'])
</script>
<style lang="scss" scoped>
@import '@/styles/_variables.scss';
$button-radius: 999px;
$button-height-sm: map-get($btn-height, 'sm');
$button-height-md: map-get($btn-height, 'md');
$button-primary-light: mix(#fff, $primary-color, 92%);
$button-primary-light-hover: mix(#fff, $primary-color, 86%);
$button-warning-light: mix(#fff, $warning-color, 88%);
$button-warning-light-hover: mix(#fff, $warning-color, 82%);
$button-info-bg: map-get($bg-color, 'secondary');
$button-info-border: map-get($border-color, 'light');
$button-disabled-bg: map-get($bg-color, 'secondary');
$button-disabled-color: map-get($text-color, 'disabled');

.custom-button {
  display: inline-block;
  vertical-align: middle;

  .n-button {
    min-height: $button-height-sm;
    padding: map-get($btn-padding, 'sm');
    border-radius: $button-radius;
    font-size: map-get($font-size, 'extra-small') !important;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 0.2px;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease,
      border-color 0.2s ease, color 0.2s ease;

    &:not(.n-button--disabled):hover {
      transform: translateY(-1px);
    }

    &:not(.n-button--disabled):active {
      transform: translateY(0);
    }
  }
}

// 1. 强主按钮：报名、提交、确认等高优先级操作
.custom-primary-btn {
  background: linear-gradient(
    135deg,
    lighten($primary-color, 10%) 0%,
    $primary-color 58%,
    darken($primary-color, 6%) 100%
  );
  border: 1px solid transparent;
  color: #fff;
  box-shadow: 0 8px 18px rgba($primary-color, 0.24);

  &:hover {
    color: #fff;
    background: linear-gradient(
      135deg,
      lighten($primary-color, 7%) 0%,
      darken($primary-color, 3%) 58%,
      darken($primary-color, 10%) 100%
    );
    box-shadow: 0 10px 22px rgba($primary-color, 0.3);
  }

  &:active {
    background: darken($primary-color, 9%);
    box-shadow: 0 4px 10px rgba($primary-color, 0.22);
  }

  &.n-button--disabled {
    background: mix(#fff, $primary-color, 55%);
    border-color: transparent;
    box-shadow: none;
    color: #fff;
    opacity: 0.72;
  }
}

// 2. 标准主按钮：普通页面主操作
.custom-primary-default-btn {
  background: $primary-color;
  border: 1px solid $primary-color;
  color: #fff;
  box-shadow: 0 5px 14px rgba($primary-color, 0.18);

  &:hover {
    background: darken($primary-color, 5%);
    border-color: darken($primary-color, 5%);
    color: #fff;
    box-shadow: 0 8px 18px rgba($primary-color, 0.24);
  }

  &:active {
    background: darken($primary-color, 10%);
    border-color: darken($primary-color, 10%);
  }

  &.n-button--disabled {
    background: mix(#fff, $primary-color, 55%);
    border-color: mix(#fff, $primary-color, 55%);
    color: #fff;
    box-shadow: none;
    opacity: 0.72;
  }
}

// 3. 轻量主按钮：去答题 / 去考试 / 观看视频
.custom-primary-mini-btn {
  height: $button-height-sm;
  padding: map-get($btn-padding, 'sm');
  background: $button-primary-light;
  border: 1px solid rgba($primary-color, 0.16);
  color: $primary-color;
  box-shadow: 0 3px 10px rgba($primary-color, 0.08);

  &:hover {
    background: $button-primary-light-hover;
    border-color: rgba($primary-color, 0.28);
    color: darken($primary-color, 4%);
    box-shadow: 0 6px 14px rgba($primary-color, 0.14);
  }

  &:active {
    background: mix(#fff, $primary-color, 78%);
    border-color: rgba($primary-color, 0.34);
  }

  &.n-button--disabled {
    background: $button-disabled-bg;
    border-color: $button-info-border;
    box-shadow: none;
    color: $button-disabled-color;
    cursor: not-allowed;
    opacity: 1;
  }
}

// 4. 次强调按钮：查看课程、提醒类操作
.custom-warning-btn {
  background: $button-warning-light;
  border: 1px solid rgba($warning-color, 0.18);
  color: $warning-color;
  box-shadow: 0 3px 10px rgba($warning-color, 0.1);

  &:hover {
    background: $button-warning-light-hover;
    border-color: rgba($warning-color, 0.28);
    color: darken($warning-color, 3%);
    box-shadow: 0 6px 14px rgba($warning-color, 0.16);
  }

  &:active {
    background: mix(#fff, $warning-color, 72%);
    border-color: rgba($warning-color, 0.34);
  }

  &.n-button--disabled {
    background: mix(#fff, $warning-color, 82%);
    border-color: transparent;
    color: mix(#fff, $warning-color, 35%);
    box-shadow: none;
    opacity: 0.75;
  }
}

// 5. 中性按钮：返回、已完成、不可操作说明
.custom-info-btn {
  background: $button-info-bg;
  border: 1px solid $button-info-border;
  color: map-get($text-color, 'secondary');
  box-shadow: none;

  &:hover {
    background: darken($button-info-bg, 3%);
    border-color: map-get($border-color, 'dark');
    color: map-get($text-color, 'regular');
    box-shadow: 0 4px 12px rgba(#1e1e1e, 0.06);
  }

  &:active {
    background: darken($button-info-bg, 6%);
  }

  &.n-button--disabled {
    background: $button-disabled-bg;
    border-color: $button-info-border;
    color: map-get($text-color, 'placeholder');
    box-shadow: none;
    opacity: 0.8;
  }
}

.custom-primary-btn,
.custom-primary-default-btn {
  min-height: $button-height-md;
  padding: map-get($btn-padding, 'md');
}
</style>
